import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { tenantApi } from '../services/api';
import { Icon } from '@iconify/react';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';
import PoliModal from '../components/admin/PoliModal';
import DeleteConfirmationModal from '../components/admin/DeleteConfirmationModal';

const ClinicAdminPoli = () => {
    const { slug: routeSlug } = useParams();
    const storedSlug = (() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return (
                user?.klinik?.slug ||
                user?.clinic?.slug ||
                user?.slug ||
                null
            );
        } catch {
            return null;
        }
    })();
    const slug = routeSlug || storedSlug;

    const [poliList, setPoliList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPoli, setEditingPoli] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [poliToDelete, setPoliToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pageError, setPageError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'nama_poli', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const getPoliId = (poli) => {
        const rawId = poli?.id_poli;
        if (rawId === null || rawId === undefined || rawId === '') return null;
        const parsed = Number(rawId);
        return Number.isFinite(parsed) ? parsed : null;
    };

    // Fetch data poli
    const fetchPoli = async () => {
        if (!slug) return;
        try {
            setIsLoading(true);
            setPageError('');
            const response = await tenantApi(slug).poli.getAll();
            if (response.data.success) {
                const normalized = Array.isArray(response.data.data)
                    ? response.data.data
                        .map((item) => ({
                            ...item,
                            id_poli: item?.id_poli ?? null,
                        }))
                        .filter((item) => getPoliId(item) !== null)
                    : [];
                setPoliList(normalized);
            }
        } catch (error) {
            console.error("Error fetching poli:", error);
            setPageError(error.response?.data?.message || 'Gagal memuat data poli.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!slug) return;
        fetchPoli();
    }, [slug]);

    const handleOpenModal = () => {
        setEditingPoli(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPoli(null);
    };

    const toErrorMessage = (error, fallbackText) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        const validationErrors = error?.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join('\n')
            : null;
        return validationErrors || message || (status ? `${fallbackText} (HTTP ${status})` : fallbackText);
    };

    const handleEditPoli = async (poliId) => {
        if (!poliId) {
            alert('ID poli tidak ditemukan pada data baris ini.');
            return;
        }

        // Use the row data already loaded from index to avoid an extra GET detail call
        // that can fail due route/context mismatch while the row itself is valid.
        const row = poliList.find((item) => getPoliId(item) === Number(poliId));
        if (!row) {
            alert('Data poli tidak ditemukan di tabel saat ini.');
            return;
        }

        setEditingPoli({
            ...row,
            id_poli: getPoliId(row),
        });
        setIsModalOpen(true);
    };

    const handleDeletePoli = (poli) => {
        if (!getPoliId(poli)) {
            alert('ID poli tidak ditemukan pada data yang akan dihapus.');
            return;
        }
        setPoliToDelete(poli);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (poliToDelete) {
            const poliId = getPoliId(poliToDelete);
            if (!poliId) {
                alert('ID poli tidak ditemukan pada data yang akan dihapus.');
                return;
            }

            try {
                setIsSubmitting(true);
                await tenantApi(slug).poli.delete(poliId);
                await fetchPoli();
                setShowDeleteModal(false);
                setPoliToDelete(null);
            } catch (error) {
                console.error("Error deleting poli:", error);
                alert(toErrorMessage(error, 'Gagal menghapus poli.'));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleSubmit = async (formData) => {
        try {
            setIsSubmitting(true);
            const dataToSend = {
                nama_poli: formData.nama_poli,
                deskripsi: formData.deskripsi || null,
                jumlah_dokter: formData.jumlah_dokter === '' ? 0 : Number(formData.jumlah_dokter || 0),
                jam_operasional: formData.jam_operasional || null,
                status: formData.status || 'aktif',
            };

            if (editingPoli) {
                const poliId = getPoliId(editingPoli);
                if (!poliId) {
                    throw new Error('ID poli tidak ditemukan saat update.');
                }
                await tenantApi(slug).poli.update(poliId, dataToSend);
            } else {
                await tenantApi(slug).poli.create(dataToSend);
            }
            await fetchPoli();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving poli:", error);
            alert(toErrorMessage(error, 'Terjadi kesalahan saat menyimpan data poli.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get dokter count for display
    const getDokterCount = (dokter) => {
        return dokter?.length || 0;
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedList = [...poliList].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        let av = a?.[sortConfig.key];
        let bv = b?.[sortConfig.key];
        if (sortConfig.key === 'dokter_count') {
            av = a.jumlah_dokter ?? getDokterCount(a.dokter);
            bv = b.jumlah_dokter ?? getDokterCount(b.dokter);
        }
        return String(av ?? '').localeCompare(String(bv ?? ''), 'id', { numeric: true }) * direction;
    });

    const totalPages = Math.max(1, Math.ceil(sortedList.length / perPage));
    const paginatedList = sortedList.slice((currentPage - 1) * perPage, currentPage * perPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [poliList.length]);

    if (!routeSlug && storedSlug) {
        return <Navigate to={`/${storedSlug}/clinic-admin/poli`} replace />;
    }

    if (!slug) {
        return (
            <ClinicAdminLayout activePage="poli" title="Poli Klinik">
                <p className="text-sm text-gray-500">
                    Slug klinik tidak ditemukan. Gunakan URL <code className="bg-gray-100 px-1 rounded">/nama-klinik/clinic-admin/poli</code>.
                </p>
            </ClinicAdminLayout>
        );
    }

    return (
        <ClinicAdminLayout activePage="poli" title="Poli Klinik">
            {/* Poli Table Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Poli</h3>
                    <button
                        onClick={handleOpenModal}
                        className="px-6 py-3 bg-gradient-to-r from-[#06B6D4] to-[#038DA5] text-white 
                        font-bold text-sm rounded-xl hover:opacity-90 transition-all shadow-lg 
                        shadow-cyan-500/30 flex items-center gap-2"
                    >
                        <Icon icon="material-symbols:add" className="text-lg" />
                        Tambah Poli
                    </button>
                </div>

                {pageError && (
                    <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {pageError}
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => handleSort('nama_poli')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama Poli</th>
                                <th onClick={() => handleSort('dokter_count')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Jumlah Dokter</th>
                                <th onClick={() => handleSort('jam_operasional')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Jam Operasional</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400">Loading...</td>
                                </tr>
                            ) : paginatedList.map((poli) => (
                                <tr key={poli.id_poli} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-cyan-50 rounded-full flex-shrink-0 flex items-center justify-center">
                                                <Icon icon="material-symbols:medical-services-outline" className="text-[#06B6D4] text-xl" />
                                            </div>
                                            <div>
                                                <span className="font-bold text-sm text-gray-700">{poli.nama_poli}</span>
                                                {poli.deskripsi && (
                                                    <p className="text-xs text-gray-400 truncate max-w-[200px]">{poli.deskripsi}</p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-bold text-gray-700">{poli.jumlah_dokter ?? getDokterCount(poli.dokter)}</td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{poli.jam_operasional}</td>
                                    <td className="px-6 py-5">
                                        <div className={`w-[80px] h-7 flex items-center justify-center text-white text-[10px] font-black rounded-lg ${poli.status === 'aktif'
                                            ? 'bg-[#0DBC94]'
                                            : 'bg-[#F64E60]'
                                            }`}>
                                            {poli.status === 'aktif' ? 'Aktif' : 'Non-Aktif'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleEditPoli(poli.id_poli)}
                                                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-[#06B6D4] hover:border-[#06B6D4] transition-all"
                                            >
                                                <Icon icon="material-symbols:edit-note-outline" className="text-xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDeletePoli(poli)}
                                                className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-500 transition-all"
                                            >
                                                <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!isLoading && paginatedList.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                                        <Icon icon="material-symbols:medical-services-outline" className="text-5xl mx-auto mb-3" />
                                        <p className="text-sm font-body">Belum ada poli terdaftar</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{currentPage}/{totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Poli Modal */}
            <PoliModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                editData={editingPoli}
                isSubmitting={isSubmitting}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                itemName={poliToDelete?.nama_poli}
            />
        </ClinicAdminLayout>
    );
};

export default ClinicAdminPoli;
